import $ from 'jquery';
import Backbone from '@polleverywhere/backbone';
import URI from 'urijs';
import Job from '../job';
import PresenterInstance from '../presenter_instance';
import ParticipantInstance from '../participant_instance';
/**
 * Creates and returns a new instance using the provided instance class. If a
 * previous instance already exists and matches the type of the provided
 * instance class, then return the existing instance instead of a new one.
 * @param {PresenterInstance|ParticipantInstance}
 * @return {PresenterInstance|ParticipantInstance}
 */

function createInstance(InstanceClass) {
  if (this._instance && this._instance instanceof InstanceClass) {
    return this._instance;
  }

  return new InstanceClass({}, {
    activity: this
  });
}

function resetPollResults(poll, options) {
  poll.set(poll.parse({
    results_count: 0
  }, options), options);
}
/**
 * Methods to be mixed into every activity class instance.
 * @type {Object}
 */


var MixinMethods = {
  /**
   * Clear all activity results and return a Promise that is resolved once the
   * action is fully completed or rejected if an error occurs.
   * @param {Object} options
   * @param {string} options.name Optional name for the archive
   * @return {Promise}
   */
  clearResults: function clearResults(options) {
    var _this = this;

    if (options === void 0) {
      options = {};
    }

    if (!this.archives) {
      throw new Error('Activity must have an archive to clear results');
    }

    var promise = new Promise(function (resolve, reject) {
      return resolve();
    });
    var isMqa = !!this.polls;

    if (isMqa) {
      // MQAs typically need to clear results using a job b/c they are clearing
      // multiple activities.
      promise = Job.create(this.archives.url(), {
        name: options.name
      }).promise.then(function (data) {
        _this.polls.each(function (poll) {
          return resetPollResults(poll, options);
        });

        return data;
      });
    } else {
      // If no results exist, do nothing and return a resolved promise.
      if (!this.get('results_count')) {
        return promise;
      } // For SQAs, simply create and save a new archive.


      var data = options.name ? {
        name: options.name
      } : {};
      var newArchive = this.archives.add(data);
      promise = newArchive.save(null, {
        silent: true
      }).then(function (data, status, $xhr) {
        if (!data) {
          return;
        }

        resetPollResults(_this, options);

        _this.trigger('clear');

        return data;
      });
    } // We have to convert native promise to $.Deferred object because consumers
    // are still using Deferred-specific methods like `done()`.
    // TODO: When we switch over to using native Promises, remove this wrapping.


    return $.when(promise).promise();
  },

  /**
   * Sends a request to duplicate this activity and returns a Promise.
   * @param {Object} options Passed into the ajax request
   * @return {Promise}
   */
  duplicate: function duplicate(options) {
    if (options === void 0) {
      options = {};
    }

    options = Object.assign({}, options, {
      url: URI(this.instance.url()).segment('duplicate').toString(),
      method: 'post',
      dataType: 'json'
    });
    return Backbone.ajax(options);
  }
};
/**
 * Properties to be mixed into every activity class instance.
 * @type {Object}
 */

var MixinProperties = {
  /**
   * Return the collection for archived results. Consumers can access this
   * collection to fetch a list of archives and individual archived result data.
   * @return {Backbone.Collection}
   */
  archives: {
    get: function get() {
      return this.instance.archives;
    }
  },

  /**
   * Returns the appropriate instance type model for this activity. Determines
   * which instance type to use by inspecting which keys exist on the activity.
   * @type {Backbone.Model}
   */
  instance: {
    get: function get() {
      var presenterKey = this.get('presenter_key');
      var participantKey = this.get('participant_key');
      var sharedKey = this.get('shared_key'); // We still support `permalink` passed as the initial `presenter_key`. If
      // model hydrates and does not have presenter rights, the key will match
      // `shared_key` and should instead generate a participant instance.

      if ((!presenterKey || presenterKey === sharedKey) && participantKey) {
        this._instance = createInstance.call(this, ParticipantInstance);
      } else {
        // To support legacy models that are still constructing activities by
        // passing in `permalink` or new activities without one, send the
        // initial fetch/save to the presenter instance. After initial
        // hydration, all keys will be available and will self-correct to
        // return the proper instance type on future calls.
        this._instance = createInstance.call(this, PresenterInstance);
      }

      return this._instance;
    },
    set: function set(instance) {
      this._instance = instance;
    }
  }
};
export default function (self) {
  Object.defineProperties(self, MixinProperties);
  return MixinMethods;
}